import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormControl,
  FormControlLabel,
  Switch,
  CircularProgress,
  Divider,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CancelIcon from "@mui/icons-material/Cancel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import useFtpConnections from "../hooks/useFtpConnections";
import { useMutation } from "@apollo/client";
import {
  addFtpConnectionMutation,
  updateFtpConnectionMutation,
  testFtpConnectionMutation,
} from "../mutations";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    minHeight: "70vh",
    maxHeight: "90vh",
    minWidth: "70vw",
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(1),
  top: theme.spacing(1),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  fontWeight: 500,
}));

const ConnectionsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "60vh",
});

const ConnectionsList = styled(Paper)(({ theme }) => ({
  flexGrow: 1,
  overflowY: "auto",
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const DetailsContainer = styled(Box)({
  height: "60vh",
  display: "flex",
  flexDirection: "column",
});

const DetailsForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: "auto",
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(0.5, 0),
  borderRadius: theme.shape.borderRadius,
  "&.Mui-selected": {
    backgroundColor: "rgba(63, 81, 181, 0.08)",
    "&:hover": {
      backgroundColor: "rgba(63, 81, 181, 0.12)",
    },
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatusIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
}));

const ActiveIcon = styled(PowerSettingsNewIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const InactiveIcon = styled(PowerSettingsNewIcon)(({ theme }) => ({
  color: theme.palette.text.disabled,
}));

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: "flex",
  justifyContent: "space-between",
}));

const NewButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
}));

const GridContainer = styled(Grid)({
  height: "100%",
});

const ListPadding = styled(List)({
  padding: "8px",
});

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(3),
}));

const NoConnectionsBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: "center",
}));

const StatusLabel = styled(Typography)({
  marginRight: "16px",
});

const StatusIconMargin = styled(Box)({
  marginRight: "8px",
});

const TitleBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const ContentBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const EmptyStateBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const BoxHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

const FtpConnectionManager = ({ open, onClose, onConnectionsUpdated }) => {
  const { data: connections, loading, refetch } = useFtpConnections();

  const [selectedConnection, setSelectedConnection] = useState(null);
  const [connectionData, setConnectionData] = useState({
    id: null,
    clientName: "",
    host: "",
    port: 22,
    username: "",
    password: "",
    filePrefix: "",
    privateKey: "",
    status: "Active",
  });

  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [addFtpConnection] = useMutation(addFtpConnectionMutation);
  const [updateFtpConnection] = useMutation(updateFtpConnectionMutation);
  const [testFtpConnection] = useMutation(testFtpConnectionMutation);

  useEffect(() => {
    if (
      !loading &&
      connections &&
      connections.length > 0 &&
      !selectedConnection &&
      !editMode
    ) {
      handleSelectConnection(connections[0]);
    }
  }, [loading, connections, selectedConnection, editMode]);

  const handleSelectConnection = (connection) => {
    setSelectedConnection(connection);
    setConnectionData(connection);
    setEditMode(false);
    setErrors({});
    setShowPassword(false);
  };

  const handleNewConnection = () => {
    setSelectedConnection(null);
    setConnectionData({
      id: null,
      clientName: "",
      host: "",
      port: 22,
      username: "",
      password: "",
      filePrefix: "",
      privateKey: "",
      status: "Active",
    });
    setEditMode(true);
    setErrors({});
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConnectionData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!connectionData.clientName) {
      newErrors.clientName = "Client Name is required";
    }

    if (!connectionData.host) {
      newErrors.host = "Host is required";
    }

    if (!connectionData.port) {
      newErrors.port = "Port is required";
    } else if (isNaN(parseInt(connectionData.port))) {
      newErrors.port = "Port must be a number";
    }

    if (!connectionData.username) {
      newErrors.username = "Username is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const input = {
        id: connectionData.id ? parseInt(connectionData.id) : null,
        clientName: connectionData.clientName.trim(),
        host: connectionData.host.trim(),
        port: parseInt(connectionData.port),
        username: connectionData.username.trim(),
        password: connectionData.password || null,
        filePrefix: connectionData.filePrefix
          ? connectionData.filePrefix.trim()
          : "",
        privateKey: connectionData.privateKey
          ? connectionData.privateKey.trim()
          : "",
        status: connectionData.status,
      };

      console.log("Saving FTP connection:", {
        ...input,
        password: input.password ? "********" : null,
        privateKey: input.privateKey ? "********" : null,
      });

      let result;

      if (connectionData.id) {
        result = await updateFtpConnection({ variables: { input } });
      } else {
        result = await addFtpConnection({ variables: { input } });
      }

      console.log("FTP connection saved result:", result);

      await refetch();

      if (onConnectionsUpdated) {
        onConnectionsUpdated();
      }

      setSnackbarSeverity("success");
      setSnackbarMessage(
        `FTP connection ${
          connectionData.id ? "updated" : "created"
        } successfully.`
      );
      setSnackbarOpen(true);
      setEditMode(false);

      if (!connectionData.id) {
        const newConnections = await refetch();
        if (newConnections?.data?.getFtpConnections) {
          const newConnection = newConnections.data.getFtpConnections.find(
            (conn) => conn.clientName === connectionData.clientName
          );
          if (newConnection) {
            handleSelectConnection(newConnection);
          }
        }
      }
    } catch (error) {
      console.error("Error saving FTP connection:", error);
      let errorMessage = error.message;

      if (error.networkError) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors.map((e) => e.message).join(", ");
      }

      setSnackbarSeverity("error");
      setSnackbarMessage(`Error saving FTP connection: ${errorMessage}`);
      setSnackbarOpen(true);
    }

    setSaving(false);
  };

  const handleTestConnection = async () => {
    if (!connectionData.clientName) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please provide a Client Name before testing.");
      setSnackbarOpen(true);
      return;
    }

    setTesting(true);

    try {
      console.log(
        "Testing FTP connection for client:",
        connectionData.clientName
      );

      const result = await testFtpConnection({
        variables: { clientName: connectionData.clientName.trim() },
      });

      console.log("Test connection result:", result);

      if (result.data.testFtpConnection.status === 200) {
        setSnackbarSeverity("success");
        setSnackbarMessage("Connection test successful!");
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage(
          `Connection test failed: ${
            result.data.testFtpConnection.statusText || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error("Error testing connection:", error);
      let errorMessage = error.message;

      if (error.networkError) {
        errorMessage =
          "Network error. Please check your connection and try again.";
      }

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMessage = error.graphQLErrors.map((e) => e.message).join(", ");
      }

      setSnackbarSeverity("error");
      setSnackbarMessage(`Error testing connection: ${errorMessage}`);
    }

    setSnackbarOpen(true);
    setTesting(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <StyledDialog
        open={open}
        onClose={() => {
          if (editMode) {
            if (
              window.confirm(
                "Are you sure you want to close? Any unsaved changes will be lost."
              )
            ) {
              onClose();
            }
          } else {
            onClose();
          }
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Typography variant="h6">Manage SFTP Connections</Typography>
          <CloseButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </DialogTitle>

        <DialogContent dividers>
          <GridContainer container spacing={2}>
            {/* Connections List */}
            <Grid item xs={4}>
              <ConnectionsContainer>
                <BoxHeader>
                  <Typography variant="subtitle1">Connections</Typography>
                  <ActionButton
                    startIcon={<AddIcon />}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onClick={handleNewConnection}
                  >
                    New
                  </ActionButton>
                </BoxHeader>
                <ConnectionsList variant="outlined">
                  {loading ? (
                    <LoadingContainer>
                      <CircularProgress size={24} />
                    </LoadingContainer>
                  ) : connections && connections.length > 0 ? (
                    <ListPadding component="nav">
                      {connections.map((connection) => (
                        <StyledListItem
                          button
                          key={connection.id}
                          selected={selectedConnection?.id === connection.id}
                          onClick={() => handleSelectConnection(connection)}
                        >
                          <ListItemText
                            primary={connection.clientName}
                            secondary={connection.host}
                            primaryTypographyProps={{ noWrap: true }}
                            secondaryTypographyProps={{ noWrap: true }}
                          />
                          <StatusIcon>
                            <Tooltip
                              title={
                                connection.status === "Active"
                                  ? "Active"
                                  : "Disabled"
                              }
                            >
                              {connection.status === "Active" ? (
                                <ActiveIcon fontSize="small" />
                              ) : (
                                <InactiveIcon fontSize="small" />
                              )}
                            </Tooltip>
                          </StatusIcon>
                        </StyledListItem>
                      ))}
                    </ListPadding>
                  ) : (
                    <NoConnectionsBox>
                      <Typography variant="body2">
                        No connections found
                      </Typography>
                    </NoConnectionsBox>
                  )}
                </ConnectionsList>
              </ConnectionsContainer>
            </Grid>

            {/* Connection Details */}
            <Grid item xs={8}>
              <DetailsContainer>
                <DetailsForm variant="outlined">
                  {connectionData ? (
                    <Box>
                      <TitleBox>
                        <Box display="flex" alignItems="center">
                          <Typography variant="h6">
                            {editMode
                              ? connectionData.id
                                ? "Edit Connection"
                                : "New Connection"
                              : "Connection Details"}
                          </Typography>
                          {!editMode && selectedConnection && (
                            <ActionButton
                              variant="outlined"
                              color="primary"
                              onClick={handleTestConnection}
                              disabled={testing || !connectionData?.clientName}
                              style={{ marginLeft: 16 }}
                            >
                              {testing ? (
                                <CircularProgress size={24} />
                              ) : (
                                "Test Connection"
                              )}
                            </ActionButton>
                          )}
                        </Box>
                        {!editMode && selectedConnection ? (
                          <ActionButton
                            color="primary"
                            onClick={() => setEditMode(true)}
                          >
                            Edit
                          </ActionButton>
                        ) : editMode ? (
                          <Box display="flex">
                            <ActionButton
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                const hasChanges =
                                  selectedConnection &&
                                  (selectedConnection.clientName !==
                                    connectionData.clientName ||
                                    selectedConnection.host !==
                                      connectionData.host ||
                                    selectedConnection.port !==
                                      connectionData.port ||
                                    selectedConnection.username !==
                                      connectionData.username ||
                                    selectedConnection.password !==
                                      connectionData.password ||
                                    selectedConnection.filePrefix !==
                                      connectionData.filePrefix ||
                                    selectedConnection.privateKey !==
                                      connectionData.privateKey ||
                                    selectedConnection.status !==
                                      connectionData.status);

                                if (
                                  !selectedConnection ||
                                  !hasChanges ||
                                  window.confirm(
                                    "Are you sure you want to cancel? Any changes will be lost."
                                  )
                                ) {
                                  if (selectedConnection) {
                                    setConnectionData(selectedConnection);
                                  }
                                  setEditMode(false);
                                  setErrors({});
                                  setShowPassword(false);
                                }
                              }}
                            >
                              Cancel
                            </ActionButton>
                            <ActionButton
                              variant="outlined"
                              color="primary"
                              onClick={handleSave}
                              disabled={saving}
                              style={{ marginLeft: 16 }}
                            >
                              {saving ? <CircularProgress size={24} /> : "Save"}
                            </ActionButton>
                          </Box>
                        ) : null}
                      </TitleBox>

                      <Divider />

                      <ContentBox>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Client Name"
                              name="clientName"
                              value={connectionData.clientName || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                              error={!!errors.clientName}
                              helperText={errors.clientName}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <Box display="flex" alignItems="center">
                                <StatusLabel variant="body1">
                                  Status:
                                </StatusLabel>
                                {editMode ? (
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          connectionData.status === "Active"
                                        }
                                        onChange={(e) =>
                                          setConnectionData({
                                            ...connectionData,
                                            status: e.target.checked
                                              ? "Active"
                                              : "Disabled",
                                          })
                                        }
                                        color="primary"
                                      />
                                    }
                                    label=""
                                  />
                                ) : (
                                  <Box display="flex" alignItems="center">
                                    <StatusIconMargin>
                                      {connectionData.status === "Active" ? (
                                        <ActiveIcon fontSize="small" />
                                      ) : (
                                        <InactiveIcon fontSize="small" />
                                      )}
                                    </StatusIconMargin>
                                  </Box>
                                )}
                                <Typography>{connectionData.status}</Typography>
                              </Box>
                            </FormControl>
                          </Grid>

                          <Grid item xs={12} sm={8}>
                            <TextField
                              label="Host"
                              name="host"
                              value={connectionData.host || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                              error={!!errors.host}
                              helperText={errors.host}
                            />
                          </Grid>

                          <Grid item xs={12} sm={4}>
                            <TextField
                              label="Port"
                              name="port"
                              type="number"
                              value={connectionData.port || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                              error={!!errors.port}
                              helperText={errors.port}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Username"
                              name="username"
                              value={connectionData.username || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                              error={!!errors.username}
                              helperText={errors.username}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Password"
                              name="password"
                              type={showPassword ? "text" : "password"}
                              value={connectionData.password || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                              InputProps={{
                                endAdornment: editMode && (
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    {showPassword ? (
                                      <VisibilityOffIcon />
                                    ) : (
                                      <VisibilityIcon />
                                    )}
                                  </IconButton>
                                ),
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="File Prefix (Folder)"
                              name="filePrefix"
                              value={connectionData.filePrefix || ""}
                              onChange={handleInputChange}
                              fullWidth
                              disabled={!editMode}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="RSA Private Key"
                              name="privateKey"
                              value={connectionData.privateKey || ""}
                              onChange={handleInputChange}
                              fullWidth
                              multiline
                              rows={4}
                              disabled={!editMode}
                              placeholder={
                                editMode ? "Paste RSA private key here..." : ""
                              }
                              InputProps={{
                                endAdornment: editMode &&
                                  connectionData.privateKey && (
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setConnectionData({
                                          ...connectionData,
                                          privateKey: "",
                                        })
                                      }
                                      title="Clear private key"
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  ),
                              }}
                              variant="outlined"
                            />
                            {!editMode && connectionData.privateKey && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                Private key is set
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </ContentBox>
                    </Box>
                  ) : (
                    <EmptyStateBox>
                      <Typography variant="body1">
                        Select a connection or create a new one
                      </Typography>
                    </EmptyStateBox>
                  )}
                </DetailsForm>
              </DetailsContainer>
            </Grid>
          </GridContainer>
        </DialogContent>

        <DialogActions>
          <ActionButton onClick={onClose} color="primary" variant="outlined">
            Close
          </ActionButton>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default FtpConnectionManager;
